import { CoreModal, CoreModalProps } from '../../../_core/components/_ui/core-modal';
import { useForm } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import { FC, useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Box, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CoreButton, CoreIconButton } from '../../../_core/components/_ui/core-button';
import { DownloadIcon } from '../../../_core/constants/icons.constants';
import { mainTitleModalWindowStyles } from '../../../_core/styles/modal-form.styles';
import { ProgressBar } from '../../../_core/components/_ui/progress-bar';
import { useOpenModalForm } from '../../hooks/modal-forms/use-open-modal-form.hook';
import CopyToClipboard from 'react-copy-to-clipboard';
import { ImportedWarningsTable } from '../../../_core/components/imported-warnings-table';
import {
	useGetTemplateUrlQuery,
	useCreateFamilyArrayMutation,
	useImportFamiliesMutation,
} from '../../../../store/api/families.api';
import { FamilyColumnName } from '../../hooks/table-columns/families-table-columns.hook';
import { FamiliesTable } from '../families-table';
import { ExclamationTooltip } from '../../../_core/components/_ui/exclamation-tooltip';

export type FamilyImportModalProps = CoreModalProps & {
	submitAction(values: FormData): Promise<void>;
	closeModal(): void;
	districtId?: string | null;
};

const ImportErrorTable: FC<any> = (props) => {
	const { data } = props;
	return (
		<Box
			sx={{
				maxHeight: '200px',
				display: 'flex',
				width: '100%',
				borderBottom: '1px solid #ccc',
				borderLeft: '1px solid #ccc',
				borderRight: '1px solid #ccc',
			}}
		>
			{data?.length ? (
				<ImportedWarningsTable
					data={data}
					importedWarningsColumnsList={['rowIndex', 'columnName', 'columnValue', 'message']}
					loading={false}
				/>
			) : (
				<Box sx={{ height: '200px', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
						<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
							<rect x="1" y="1" width="30" height="30" rx="15" stroke="#36AB55" stroke-width="2" />
							<path
								d="M10.75 16.75L14.25 20.25L21.25 12.75"
								stroke="#36AB55"
								stroke-width="2"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
						</svg>
						<Typography>There are no import errors</Typography>
					</Box>
				</Box>
			)}
		</Box>
	);
};

const ImportWarningTable: FC<any> = (props) => {
	const { data, handleOpenModalEdit } = props;
	return (
		<Box
			sx={{
				maxHeight: '200px',
				display: 'flex',
				width: '100%',
				borderBottom: '1px solid #ccc',
				borderLeft: '1px solid #ccc',
				borderRight: '1px solid #ccc',
			}}
		>
			{data?.length ? (
				<ImportedWarningsTable
					data={data}
					importedWarningsColumnsList={['rowIndex', 'columnName', 'columnValue', 'message', 'actions']}
					loading={false}
					onEdit={handleOpenModalEdit}
				/>
			) : (
				<Box sx={{ height: '200px', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
						<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
							<rect x="1" y="1" width="30" height="30" rx="15" stroke="#36AB55" stroke-width="2" />
							<path
								d="M10.75 16.75L14.25 20.25L21.25 12.75"
								stroke="#36AB55"
								stroke-width="2"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
						</svg>
						<Typography>There are no warnings</Typography>
					</Box>
				</Box>
			)}
		</Box>
	);
};

const familyColumnsList: FamilyColumnName[] = [
	'importedRow',
	'fullName',
	'email',
	'phoneNumber',
	'location',
	'preferedLanguage',
];

function a11yProps(index: number) {
	return {
		id: `full-width-tab-${index}`,
		'aria-controls': `full-width-tabpanel-${index}`,
	};
}

export const FamilyImportModal: React.FC<FamilyImportModalProps> = (props) => {
	const { submitAction, closeModal, districtId, ...modalProps } = props;
	const [t] = useTranslation();
	const [importFamilies, { data: readFileResult, isLoading: isReadingFile }] = useImportFamiliesMutation();
	const [createFamiliesArray, { data: importedResult, isLoading: isImportLoading }] = useCreateFamilyArrayMutation();
	const [importData, setImportData] = useState(readFileResult);

	useEffect(() => {
		setImportData(readFileResult);
	}, [readFileResult]);

	const [tabValue, setTabValue] = useState<null | number>(0);

	const { setValue, trigger } = useForm();
	const [file, setFile] = useState<File | null>(null);
	const [formError, setFormError] = useState<Error | null>(null);

	const { data: templateData } = useGetTemplateUrlQuery();

	const { enqueueSnackbar } = useSnackbar();

	const onDrop = useCallback(
		(acceptedFiles: File[]) => {
			const selectedFile = acceptedFiles[0];
			setFile(selectedFile);
			setValue('csvFile', selectedFile);
			trigger('csvFile');
		},
		[setValue, trigger]
	);

	const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
		onDrop,
		multiple: false,
		accept: {
			'text/*': ['.csv'],
		},
	});

	const handleReadFileImport = async () => {
		if (!file) return;

		if (!file.name.endsWith('.csv') || file.type !== 'text/csv') {
			enqueueSnackbar(t('families.invalid-file-type'), {
				variant: 'error',
				autoHideDuration: 3000,
			});

			return;
		}

		const formData = new FormData();
		formData.append('file', file);
		try {
			try {
				await importFamilies({ formData, districtId }).unwrap();
			} catch (error: any) {
				console.error('File Import Error:', error);
				setFormError(error);
			}
		} catch (error) {
			enqueueSnackbar('Failed to read the file.', { variant: 'error' });
		}
	};

	const handleFileImport = async () => {
		await createFamiliesArray(importData?.success).unwrap();
	};

	const featchActionEdit = async (data: any) => {
		setImportData((prevImportData) => {
			if (prevImportData) {
				return {
					...prevImportData,
					success: prevImportData?.success?.map((el: any) => {
						if (el.rowIndex === data.importedRow) {
							return data;
						}
						return el;
					}),
					warnings: prevImportData?.warnings?.map((el: any) => {
						if (el.rowIndex === data.importedRow) {
							return { ...el, createdFamily: data };
						}
						return el;
					}),
				};
			} else {
				return prevImportData;
			}
		});
	};

	const handleEditFileImport = useOpenModalForm({
		featchAction: featchActionEdit,
		isCreate: true,
		districtId,
		submitButtonName: t('families.forms.buttons.save'),
		toastText: 'Family contact was successfully updated',
	});

	const districtError = readFileResult?.errors?.find((error) => error.type === 'district');
	const badRequestErrors = readFileResult?.errors
		?.filter((error) => error.type !== 'district')
		?.sort((a: any, b: any) => a.rowIndex - b.rowIndex);
	const warnings = importData?.warnings?.length
		? [...importData.warnings].sort((a: any, b: any) => a.rowIndex - b.rowIndex)
		: [];
	const ready = readFileResult?.success?.length
		? [
				...readFileResult.success.filter(
					(successEl) => !warnings.some((warningEl) => warningEl.rowIndex === successEl.rowIndex)
				),
		  ].sort((a: any, b: any) => a.rowIndex - b.rowIndex)
		: [];

	const handleChange = (event: React.SyntheticEvent, newValue: number) => {
		setTabValue(newValue);
	};

	const warnCopyText = () => {
		enqueueSnackbar('Text is copied', {
			variant: 'info',
			autoHideDuration: 3000,
		});
	};

	return (
		<CoreModal
			modalWidth={importedResult ? 600 : readFileResult ? 1200 : 600}
			{...modalProps}
			bodySx={{ pt: 0 }}
			paperSx={{ width: '100%' }}
		>
			<Typography variant="h5" sx={mainTitleModalWindowStyles}>
				{'Import Families'}
			</Typography>

			{formError ? (
				<Typography variant="body1" color="error">
					{formError?.message}
				</Typography>
			) : districtError ? (
				<Box>{districtError.message}</Box>
			) : importedResult ? (
				<Box sx={{ height: '100%' }}>
					<Typography sx={{ mb: 1, fontFamily: 'Rubik', fontSize: '12px', fontWeight: '400', lineHeight: '160%' }}>
						{`${importedResult?.success?.length} of ${
							importedResult?.success?.length + importedResult?.failed?.length
						} family records were imported.`}
					</Typography>
					{importedResult?.failed?.length ? (
						<Box>
							<Typography sx={{ mb: 0, fontFamily: 'Rubik', fontSize: '12px', fontWeight: '400', lineHeight: '160%' }}>
								{`${importedResult?.failed?.length} family records weren’t imported due to unexpected errors.`}
							</Typography>
							<CopyToClipboard
								text={importedResult?.failed.map((el: any) => el.error).join('\n') ?? ''}
								onCopy={warnCopyText}
							>
								<Box
									sx={{
										display: 'flex',
										width: '100%',
										padding: '12px 16px',
										flexDirection: 'column',
										gap: '8px',
										borderRadius: '8px',
										backgroundColor: '#EDEEF3',
										maxHeight: '170px',
									}}
								>
									<Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
										<Typography
											sx={{
												fontFamily: 'Rubik',
												fontSize: '12px',
												fontWeight: '400',
												lineHeight: '160%',
											}}
										>
											Error log
										</Typography>
										<Tooltip title={t('buttons.tooltips.copy')}>
											<CoreIconButton>
												<svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
													<path
														d="M4.6665 6.94463C4.6665 6.47307 4.85383 6.02083 5.18727 5.68739C5.52071 5.35395 5.97295 5.16663 6.4445 5.16663H12.2218C12.4553 5.16663 12.6865 5.21262 12.9022 5.30197C13.118 5.39132 13.314 5.52229 13.4791 5.68739C13.6442 5.85249 13.7751 6.0485 13.8645 6.26421C13.9538 6.47993 13.9998 6.71114 13.9998 6.94463V12.722C13.9998 12.9554 13.9538 13.1867 13.8645 13.4024C13.7751 13.6181 13.6442 13.8141 13.4791 13.9792C13.314 14.1443 13.118 14.2753 12.9022 14.3646C12.6865 14.454 12.4553 14.5 12.2218 14.5H6.4445C6.21101 14.5 5.97981 14.454 5.76409 14.3646C5.54838 14.2753 5.35237 14.1443 5.18727 13.9792C5.02217 13.8141 4.8912 13.6181 4.80185 13.4024C4.71249 13.1867 4.6665 12.9554 4.6665 12.722V6.94463Z"
														stroke="#282E4A"
														stroke-width="1.5"
														stroke-linecap="round"
														stroke-linejoin="round"
													/>
													<path
														d="M2.67467 11.658C2.47 11.5417 2.29977 11.3733 2.18127 11.1699C2.06277 10.9665 2.00023 10.7354 2 10.5V3.83333C2 3.1 2.6 2.5 3.33333 2.5H10C10.5 2.5 10.772 2.75667 11 3.16667"
														stroke="#282E4A"
														stroke-width="1.5"
														stroke-linecap="round"
														stroke-linejoin="round"
													/>
												</svg>
											</CoreIconButton>
										</Tooltip>
									</Box>
									<Box sx={{ maxHeight: '100%', overflowY: 'auto' }}>
										{importedResult?.failed.map((el: any) => (
											<Typography
												sx={{
													fontFamily: 'Rubik',
													fontSize: '12px',
													fontWeight: '400',
													lineHeight: '160%',
												}}
											>
												{el.error}
											</Typography>
										))}
									</Box>
								</Box>
							</CopyToClipboard>
						</Box>
					) : null}
					<Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', mt: 5 }}>
						<CoreButton contraModeStyleButton onClick={closeModal}>
							{t('engagements.forms.buttons.confirm')}
						</CoreButton>
					</Box>
				</Box>
			) : readFileResult ? (
				<Box sx={{ height: '100%' }}>
					<Typography sx={{ mb: 1, fontFamily: 'Rubik', fontSize: '12px', fontWeight: '400', lineHeight: '160%' }}>
						{`${readFileResult?.importedCount - readFileResult?.errors?.length} of ${
							readFileResult?.importedCount
						} family records are ready to import.`}
					</Typography>

					<Tabs
						value={tabValue}
						onChange={handleChange}
						TabIndicatorProps={{ style: { display: 'none' } }}
						variant="fullWidth"
					>
						<Tab
							label={
								<Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
									<Typography
										sx={{ color: '#EE202C', fontSize: '12px', fontWeight: 500 }}
									>{`Import Errors (${badRequestErrors?.length})`}</Typography>
									<ExclamationTooltip
										title={
											"Rows that can’t be imported because some attributes either missing or don't match the existing data in the system."
										}
										iconSx={{ ml: 1 }}
										placement="top-start"
									/>
								</Box>
							}
							{...a11yProps(0)}
							sx={{
								flex: 1,
								color: (theme) => theme.colors.red,
								borderTopLeftRadius: 8,
								borderTopRightRadius: 8,
								border: 'none',
								borderBottom: '1px solid #ccc',
								backgroundColor: (theme) => theme.colors.grayLight,
								'&.Mui-selected': {
									backgroundColor: '#ffffff',
									borderTop: '1px solid #ccc',
									borderLeft: '1px solid #ccc',
									borderRight: '1px solid #ccc',
									borderBottom: 'none',
									color: (theme) => theme.colors.red,
								},
							}}
						/>
						<Tab
							label={
								<Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
									<Typography
										sx={{ color: '#EC9D1D', fontSize: '12px', fontWeight: 500 }}
									>{`Warning (${warnings?.length})`}</Typography>
									<ExclamationTooltip
										title={
											'Contains rows with values that are either missing, or don’t match existing data in the system.'
										}
										iconSx={{ ml: 1 }}
										placement="top-start"
									/>
								</Box>
							}
							{...a11yProps(1)}
							sx={{
								flex: 1,
								color: '#EC9D1D',
								borderTopLeftRadius: 8,
								borderTopRightRadius: 8,
								border: 'none',
								borderBottom: '1px solid #ccc',
								backgroundColor: (theme) => theme.colors.grayLight,
								'&.Mui-selected': {
									backgroundColor: '#ffffff',
									borderTop: '1px solid #ccc',
									borderLeft: '1px solid #ccc',
									borderRight: '1px solid #ccc',
									borderBottom: 'none',
									color: '#EC9D1D',
								},
							}}
						/>
						<Tab
							label={
								<Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
									<Typography
										sx={{ color: '#36AB55', fontSize: '12px', fontWeight: 500 }}
									>{`Ready (${ready?.length})`}</Typography>
									<ExclamationTooltip
										title={'These rows passed all checks and ready to be imported into the system.'}
										iconSx={{ ml: 1 }}
										placement="top-start"
									/>
								</Box>
							}
							{...a11yProps(1)}
							sx={{
								flex: 1,
								color: '#36AB55',
								borderTopLeftRadius: 8,
								borderTopRightRadius: 8,
								border: 'none',
								borderBottom: '1px solid #ccc',
								backgroundColor: (theme) => theme.colors.grayLight,
								'&.Mui-selected': {
									backgroundColor: '#ffffff',
									borderTop: '1px solid #ccc',
									borderLeft: '1px solid #ccc',
									borderRight: '1px solid #ccc',
									borderBottom: 'none',
									color: '#36AB55',
								},
							}}
						/>
					</Tabs>

					{tabValue === 0 ? (
						<ImportErrorTable data={badRequestErrors} />
					) : tabValue === 1 ? (
						<ImportWarningTable data={warnings} handleOpenModalEdit={handleEditFileImport} />
					) : tabValue === 2 ? (
						ready?.length ? (
							<Box
								sx={{ height: '200px', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
							>
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
									<Typography>There are no ready records</Typography>
								</Box>
							</Box>
						) : (
							<FamiliesTable
								familyColumnsList={familyColumnsList}
								allowedFamilyActions={[]}
								isSorted={false}
								data={ready}
								loading={false}
							/>
						)
					) : null}
					<Box mt={3} display="flex" justifyContent="flex-end">
						<CoreButton onClick={closeModal} variant="secondary" sx={{ marginRight: 1 }}>
							{t('families.forms.buttons.cancel')}
						</CoreButton>
						<CoreButton contraModeStyleButton onClick={handleFileImport}>
							{t('families.forms.buttons.import')}
						</CoreButton>
					</Box>
				</Box>
			) : isReadingFile || isImportLoading ? (
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					<Typography>{isReadingFile ? 'Start reading file...' : 'Start importing...'}</Typography>
					<ProgressBar fullWidth />
				</Box>
			) : (
				<Box component="form">
					<Box
						{...getRootProps()}
						sx={{
							border: '2px dashed #cccccc',
							borderRadius: '10px',
							textAlign: 'center',
							padding: '20px',
							bgcolor: isDragActive ? '#e0f7fa' : '#ffffff',
							transition: 'background-color 0.3s',
							mb: 2,
						}}
					>
						<input {...getInputProps()} />
						{file ? (
							<Typography variant="body1">{file.name}</Typography>
						) : (
							<Typography variant="body2">{t('providers.select-file')}</Typography>
						)}
						<CoreButton variant="secondary" onClick={open} sx={{ my: 2 }}>
							{t('providers.choose-file')}
						</CoreButton>
					</Box>

					<a href={templateData?.url} style={{ textDecoration: 'none' }}>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'row',
								width: '100%',
								mb: 2,
							}}
						>
							<Typography>{t('families.import-template')}</Typography>
							<Box sx={{ ml: 1 }}>
								<DownloadIcon />
							</Box>
						</Box>
					</a>
					<Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
						<CoreButton onClick={closeModal} variant="secondary" sx={{ marginRight: 1 }}>
							{t('families.forms.buttons.cancel')}
						</CoreButton>
						<CoreButton type="submit" contraModeStyleButton onClick={handleReadFileImport} disabled={!file}>
							{t('families.forms.buttons.import')}
						</CoreButton>
					</Box>
				</Box>
			)}
		</CoreModal>
	);
};
